import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "./ServiceRequestForm.css"
import { supabase } from "../lib/supabase"
import { generateTimeSlots, getAvailableSlots, parseTimeToDisplay, formatLocalDate } from "../utils/timeSlots"
import { getBookedTimeSlots, amendAppointment, cancelAppointment } from "../lib/publicQueries"
import { useScrollToError } from "../hooks/useScrollToError"

const SERVICE_LABELS = {
  "medication-review": "Medication Review",
  "flu-vaccination": "Flu Vaccination",
  "blood-pressure": "Blood Pressure Check",
  "smoking-support": "Smoking Cessation Support",
}

const formSchema = z.object({
  service: z.enum(["medication-review", "flu-vaccination", "blood-pressure", "smoking-support"], {
    required_error: "Please select a service",
  }),
  preferredDate: z.date({ required_error: "Preferred date is required" }).refine(
    (date) => {
      const day = date.getDay()
      return day !== 0 && day !== 6
    },
    { message: "Appointments are only available on weekdays" }
  ),
  preferredTime: z.string().min(1, { message: "Please select a time slot" }),
})

function AmendAppointmentForm({ appointment, token }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isCancelled, setIsCancelled] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [error, setError] = useState(null)
  const [availableSlots, setAvailableSlots] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      service: appointment.service,
      preferredTime: appointment.preferred_time,
    },
  })

  useScrollToError(errors, error)

  const preferredDate = watch("preferredDate")

  // Set initial date from appointment
  useEffect(() => {
    if (appointment.preferred_date) {
      const [year, month, day] = appointment.preferred_date.split("-")
      const date = new Date(year, month - 1, day)
      if (date > new Date()) {
        setValue("preferredDate", date)
      }
    }
  }, [appointment.preferred_date, setValue])

  const isWeekday = (date) => {
    const day = date.getDay()
    return day !== 0 && day !== 6
  }

  // Fetch available time slots when date changes
  useEffect(() => {
    async function fetchAvailableSlots() {
      if (!preferredDate) {
        setAvailableSlots([])
        return
      }

      setLoadingSlots(true)
      try {
        const allSlots = generateTimeSlots()
        const { data: bookedSlots, error } = await getBookedTimeSlots(preferredDate)

        if (error) {
          console.error("Error fetching booked slots:", error)
          setAvailableSlots(allSlots)
        } else {
          const available = getAvailableSlots(allSlots, bookedSlots || [])
          setAvailableSlots(available)
        }
      } catch (err) {
        console.error("Error loading time slots:", err)
        setAvailableSlots(generateTimeSlots())
      } finally {
        setLoadingSlots(false)
      }
    }

    fetchAvailableSlots()
    setValue("preferredTime", "")
  }, [preferredDate, setValue])

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const { data: result, error: amendError } = await amendAppointment(token, {
        preferredDate: data.preferredDate,
        preferredTime: data.preferredTime,
        service: data.service,
      })

      if (amendError) {
        throw new Error(amendError.message)
      }

      if (result && !result.success) {
        throw new Error(result.error || "Unable to amend appointment")
      }

      setIsSuccess(true)

      // Send amendment notification email (non-blocking)
      try {
        supabase.functions.invoke('send-notification-email', {
          body: {
            type: 'service_amendment',
            data: {
              first_name: appointment.first_name,
              last_name: appointment.last_name,
              email: appointment.email,
              amendment_token: appointment.amendment_token,
              service: data.service,
              preferred_date: data.preferredDate instanceof Date
                ? formatLocalDate(data.preferredDate)
                : data.preferredDate,
              preferred_time: data.preferredTime,
              old_service: result.old_service,
              old_preferred_date: result.old_date,
              old_preferred_time: result.old_time,
            },
          },
        })
      } catch (emailError) {
        console.error('Amendment notification email failed:', emailError)
      }
    } catch (err) {
      console.error("Error amending appointment:", err)
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = async () => {
    setIsCancelling(true)
    setError(null)

    try {
      const { data: result, error: cancelError } = await cancelAppointment(token)

      if (cancelError) {
        throw new Error(cancelError.message)
      }

      if (result && !result.success) {
        throw new Error(result.error || "Unable to cancel appointment")
      }

      setIsCancelled(true)

      // Send cancellation notification email (non-blocking)
      try {
        supabase.functions.invoke('send-notification-email', {
          body: {
            type: 'service_cancellation',
            data: {
              first_name: result.first_name || appointment.first_name,
              last_name: result.last_name || appointment.last_name,
              email: result.email || appointment.email,
              service: result.service || appointment.service,
              preferred_date: result.preferred_date || appointment.preferred_date,
              preferred_time: result.preferred_time || appointment.preferred_time,
            },
          },
        })
      } catch (emailError) {
        console.error('Cancellation notification email failed:', emailError)
      }
    } catch (err) {
      console.error("Error cancelling appointment:", err)
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred. Please try again."
      )
      setShowCancelConfirm(false)
    } finally {
      setIsCancelling(false)
    }
  }

  if (isCancelled) {
    return (
      <div className="success-message">
        <div className="success-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3>Appointment Cancelled</h3>
        <p>Your appointment has been cancelled. You will receive a confirmation email shortly.</p>
        <p>If you'd like to book a new appointment, please visit our website.</p>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <div className="success-message">
        <div className="success-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3>Appointment Amended Successfully</h3>
        <p>Your appointment has been updated. It will need to be re-confirmed by the pharmacy, and you will receive a new confirmation email once reviewed.</p>
      </div>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="prescription-form">
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="info-box" style={{ marginBottom: "24px" }}>
          <h3 style={{ marginTop: 0 }}>Current Appointment</h3>
          <p><strong>Name:</strong> {appointment.first_name} {appointment.last_name}</p>
          <p><strong>Service:</strong> {SERVICE_LABELS[appointment.service] || appointment.service}</p>
          <p><strong>Date:</strong> {new Date(appointment.preferred_date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</p>
          <p><strong>Time:</strong> {parseTimeToDisplay(appointment.preferred_time)}</p>
        </div>

        <h3 style={{ marginBottom: "16px" }}>Amend Appointment</h3>

        <div className="form-group">
          <label htmlFor="service">Service</label>
          <select id="service" {...register("service")} className="form-control">
            <option value="">-- Please choose a service --</option>
            <option value="medication-review">Medication Review</option>
            <option value="flu-vaccination">Flu Vaccination</option>
            <option value="blood-pressure">Blood Pressure Check</option>
            <option value="smoking-support">Smoking Cessation Support</option>
          </select>
          {errors.service && <p className="error-text">{errors.service.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="preferredDate">New Preferred Date</label>
          <DatePicker
            selected={preferredDate}
            onChange={(date) => setValue("preferredDate", date)}
            minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
            filterDate={isWeekday}
            dateFormat="dd/MM/yyyy"
            className={errors.preferredDate ? "error" : ""}
            placeholderText="Select preferred date"
          />
          <small>Select a date to see available time slots (weekdays only)</small>
          {errors.preferredDate && <p className="error-text">{errors.preferredDate.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="preferredTime">New Preferred Time Slot</label>
          <select
            id="preferredTime"
            {...register("preferredTime")}
            className={errors.preferredTime ? "error" : ""}
            disabled={!preferredDate || loadingSlots}
          >
            <option value="">
              {!preferredDate
                ? "Please select a date first"
                : loadingSlots
                ? "Loading available slots..."
                : "-- Select a time slot --"}
            </option>
            {availableSlots.length === 0 && preferredDate && !loadingSlots && (
              <option value="" disabled>
                No available slots for this date
              </option>
            )}
            {availableSlots.map((slot) => (
              <option key={slot.value} value={slot.value}>
                {slot.label}
              </option>
            ))}
          </select>
          <small>30-minute appointment slots (10:00 AM - 1:00 PM, 2:00 PM - 5:00 PM)</small>
          {errors.preferredTime && <p className="error-text">{errors.preferredTime.message}</p>}
        </div>

        <button type="submit" className="button" disabled={isSubmitting || isCancelling}>
          {isSubmitting ? "Updating..." : "Amend Appointment"}
        </button>
      </form>

      <div style={{ borderTop: "1px solid #e5e7eb", margin: "32px 0", paddingTop: "32px" }}>
        <h3 style={{ marginTop: 0, marginBottom: "8px" }}>Cancel Appointment</h3>
        <p style={{ color: "#555", marginBottom: "16px" }}>
          If you no longer need this appointment, you can cancel it below. This action cannot be undone.
        </p>

        {!showCancelConfirm ? (
          <button
            type="button"
            onClick={() => setShowCancelConfirm(true)}
            disabled={isSubmitting}
            style={{
              padding: "10px 24px",
              border: "2px solid #dc2626",
              borderRadius: "6px",
              backgroundColor: "transparent",
              color: "#dc2626",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Cancel Appointment
          </button>
        ) : (
          <div style={{ backgroundColor: "#fef2f2", borderRadius: "8px", padding: "16px", border: "1px solid #fecaca" }}>
            <p style={{ margin: "0 0 12px", fontWeight: 600, color: "#991b1b" }}>
              Are you sure you want to cancel this appointment?
            </p>
            <p style={{ margin: "0 0 16px", color: "#555", fontSize: "14px" }}>
              This cannot be undone. You will need to book a new appointment if you change your mind.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isCancelling}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: "#dc2626",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: isCancelling ? "not-allowed" : "pointer",
                  opacity: isCancelling ? 0.7 : 1,
                }}
              >
                {isCancelling ? "Cancelling..." : "Yes, Cancel Appointment"}
              </button>
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                disabled={isCancelling}
                style={{
                  padding: "10px 24px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Go Back
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default AmendAppointmentForm
